// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import type { ReactNode } from "react";
import {
  Box, Button, Chip, FormControl, InputLabel, MenuItem, OutlinedInput, Select,
} from "@mui/material";
import type { FacetValues } from "./table-query.js";

export interface FacetOption { value: string; label: string }
export interface FacetDef<K extends string = string> {
  /** Request param name — must match the controller's `filterable` key. */
  key: K;
  label: string;
  options: FacetOption[];
}

export interface TableFilterBarProps<K extends string> {
  facetDefs: FacetDef<K>[];
  values: FacetValues<K>;
  onChange: (key: K, values: string[]) => void;
  onClearAll: () => void;
  /** The page's existing search field, rendered inline with the facets. */
  searchSlot?: ReactNode;
}

/**
 * The filter row that sits INLINE with a list's search box (user decision,
 * 2026-08-27 — not a panel, not per-header funnel icons), wrapping onto a
 * second line when the row runs out of width.
 *
 * Facets are multi-select; the selected values travel to the server as one
 * comma-separated param, which `Filterable` turns into an IN clause. Below
 * the controls, a chip strip names every active value so a narrowed list is
 * never mysterious — the commonest support complaint about filtered tables is
 * "rows are missing" when a filter was left on.
 *
 * Chip delete affordance: MUI's `<Chip onDelete>` renders its own delete
 * icon as an unlabelled `<svg>` inside a plain `<span>` (no button role, no
 * accessible name of its own) — a `deleteIcon` override that is just a
 * `<span aria-label>` does not get exposed as a clickable "button" to
 * accessibility queries either, since a bare `<span>` has no interactive
 * role and is never in the tab order. To give the delete affordance a real,
 * queryable accessible name (`remove ${chip.label}`) that also responds to
 * click AND keyboard, the chip itself carries `role="button"` + `tabIndex`,
 * its `aria-label` is the remove string the test looks up, and `onClick`
 * calls the same handler MUI would have wired to `onDelete`.
 *
 * Deliberately NO custom `onKeyDown` here: passing `onClick` turns MUI's
 * `<Chip>` into a `ButtonBase` (`MuiChip-clickable`), and `ButtonBase`
 * already synthesizes a click from Enter/Space keydown on its own. A custom
 * keydown handler that also called `remove()` fired the removal twice per
 * keypress — harmless-looking because `remove()` recomputes from the
 * `values` prop rather than mutating incrementally, so both calls produced
 * the same resulting array, but it doubled every downstream side effect
 * (URL write + refetch) the consuming page's `onChange` triggers. `onClick`
 * alone is both necessary and sufficient for mouse and keyboard.
 *
 * A facet with NO options is not rendered as a dropdown. That is not cosmetic:
 * `useUsers()` answers 403 with an empty array for a non-admin, so the
 * Salesperson facet would otherwise render on every list page as a select that
 * opens onto nothing — an affordance that looks broken rather than absent. It
 * is dropped from the CONTROL row only; if such a facet somehow carries a
 * selected value (a hand-typed URL), its chip below still renders so the
 * viewer can see and remove the filter narrowing their list.
 */
export function TableFilterBar<K extends string>({
  facetDefs, values, onChange, onClearAll, searchSlot,
}: TableFilterBarProps<K>) {
  const selectableFacets = facetDefs.filter((facet) => facet.options.length > 0);
  const activeChips = facetDefs.flatMap((facet) =>
    (values[facet.key] ?? []).map((value) => ({
      facetKey: facet.key,
      value,
      label: `${facet.label}: ${facet.options.find((o) => o.value === value)?.label ?? value}`,
    })),
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* `justifyContent` is spelled out rather than left to the flex default:
          the search box being LEFT-aligned on the controls row (not pushed
          toward the middle or the right) is a deliberate layout decision, and
          `crm_ai_prospects.cy.ts` asserts the computed value — which reads
          "normal", not "flex-start", when the property is merely defaulted. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-start", gap: 1.5 }}>
        {searchSlot}
        {selectableFacets.map((facet) => (
          <FormControl key={facet.key} size="small" sx={{ minWidth: 160 }}>
            <InputLabel id={`filter-${facet.key}-label`}>{facet.label}</InputLabel>
            <Select
              multiple
              labelId={`filter-${facet.key}-label`}
              id={`filter-${facet.key}`}
              label={facet.label}
              value={values[facet.key] ?? []}
              input={<OutlinedInput label={facet.label} />}
              onChange={(e) => {
                const next = e.target.value;
                onChange(facet.key, typeof next === "string" ? next.split(",") : next);
              }}
              renderValue={(selected) =>
                selected.length === 1
                  ? facet.options.find((o) => o.value === selected[0])?.label ?? selected[0]
                  : `${selected.length} selected`
              }
            >
              {facet.options.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
      </Box>

      {activeChips.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          {activeChips.map((chip) => {
            const remove = () =>
              onChange(chip.facetKey, (values[chip.facetKey] ?? []).filter((v) => v !== chip.value));
            return (
              <Chip
                key={`${chip.facetKey}:${chip.value}`}
                size="small"
                label={chip.label}
                onDelete={remove}
                role="button"
                tabIndex={0}
                aria-label={`remove ${chip.label}`}
                onClick={remove}
              />
            );
          })}
          <Button size="small" onClick={onClearAll}>Clear all</Button>
        </Box>
      )}
    </Box>
  );
}
